import * as Collapsible from '@radix-ui/react-collapsible'
import { Plus } from 'iconoir-react'
import { compact } from 'lodash'
import { observer } from 'mobx-react-lite'
import { useState, useEffect, useRef } from 'react'
import { twMerge } from 'tailwind-merge'
import { ChevronToggle } from '../../ChevronToggle'
import AddTagShadcn from 'app/components/tags/AddTagShadcn'
import Tag, { setColorCache } from 'app/components/tags/Tag'
import { Tooltip } from 'app/components/Tooltip'
import { Popover, PopoverAnchor, PopoverContent } from 'app/components/ui/popover'
import { useUpdateTags } from 'app/hooks/useUpdateTags'
import { useVisibleTags } from 'app/hooks/useVisibleTags'
import { useContactStore } from 'app/stores/StoreContext'
import type { ContactJson } from 'app/types/entities'
import { logError } from 'lib/logging'

type Props = {
  contact: ContactJson
  isAgentOnly?: boolean
}

export const ContactTagsShadcn = observer(function ContactTagsShadcn({ contact, isAgentOnly = false }: Props) {
  const contactStore = useContactStore()
  const [addTag, removeTag] = useUpdateTags(`contacts`, contact.id, false, contact.workspaceId)
  // The shared instance's tags are the source of truth (ADR-0002). `contact` is the interned
  // list.hits[i] / current, so reading `contact.tags` under observer re-renders this drawer in
  // place when a table-side edit interns new tags, and vice versa. optimisticTags is a brief
  // pre-mutation overlay, cleared once the shared instance catches up — same shape as
  // ContactPipelinesCell.
  const [optimisticTags, setOptimisticTags] = useState<string[] | undefined>()
  const contactTags = optimisticTags ?? contact.tags ?? []
  const [isExpanded, setIsExpanded] = useState(true)
  const [tagPopoverOpen, setTagPopoverOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const { visibleTags, hiddenTags } = useVisibleTags({
    tags: contactTags,
    containerRef,
    maxLines: 2,
    reservedWidth: 40,
    sortTags: false,
  })

  // Drop the optimistic value once the store-backed tags catch up.
  useEffect(() => setOptimisticTags(undefined), [contact.tags])

  const handleRemoveTag = async (tag: string) => {
    const prevTags = [...contactTags]
    const nextTags = prevTags.filter((t) => t !== tag)
    setOptimisticTags(nextTags)
    // Intern optimistically so the table row (list.hits[i]) updates in sync with the drawer,
    // not after the ~2.5s addTags/removeTags round-trip (the mutation waits on the search
    // index). Reconcile with the authoritative list on success; roll the shared instance back
    // on failure. The drawer (this observer) and the table are the one shared object.
    if (contact.id) contactStore.mergeTags(contact.id, nextTags)
    const result = await removeTag(tag)
    if (result.updatedTags) {
      if (contact.id) contactStore.mergeTags(contact.id, result.data.tags)
    } else {
      setOptimisticTags(undefined)
      if (contact.id) contactStore.mergeTags(contact.id, prevTags)
      logError(`error removing tag`)
    }
  }

  const handleAddTag = async (tag: string, color?: string) => {
    const prevTags = [...contactTags]
    const nextTags = compact([...prevTags, tag])
    // Seed the color before the optimistic chip mounts. Tag.tsx otherwise looks the color up
    // by querying TagColors, which races ahead of the row this add creates and caches silver
    // for the rest of the session.
    if (color) setColorCache(tag, `contacts`, color, contact.workspaceId)
    setOptimisticTags(nextTags)
    // Intern optimistically so the table row updates in sync with the drawer, not after the
    // slow mutation. Reconcile on success; roll back on failure.
    if (contact.id) contactStore.mergeTags(contact.id, nextTags)
    const result = await addTag(tag, color)
    if (result.updatedTags) {
      if (contact.id) contactStore.mergeTags(contact.id, result.data.tags)
    } else {
      setOptimisticTags(undefined)
      if (contact.id) contactStore.mergeTags(contact.id, prevTags)
      logError(`error adding tag`)
    }
  }

  const hasTags = contactTags.length > 0

  return (
    <Popover open={tagPopoverOpen} onOpenChange={setTagPopoverOpen} modal={false}>
      <PopoverAnchor asChild>
        <div className="flex flex-col rounded-md bg-surface p-4" data-testid="contact-tags">
          <Collapsible.Root open={isExpanded} onOpenChange={setIsExpanded} defaultOpen>
            <div className={twMerge(`flex w-full items-center justify-between`, hasTags && isExpanded && `mb-4`)}>
              <Collapsible.Trigger className={twMerge(`flex items-center gap-x-2`, hasTags ? `` : `cursor-default`)}>
                <div className="text-heading-xs text-strong">Tags</div>
                {hasTags && (
                  <>
                    <div className="flex h-5 items-center justify-center rounded border-[0.5px] border-default bg-primary-600 px-1.5">
                      <div className="text-label-xs text-on-color">{contactTags.length}</div>
                    </div>
                    <ChevronToggle open={isExpanded} />
                  </>
                )}
              </Collapsible.Trigger>
              {!isAgentOnly && (
                <AddTagShadcn
                  onAdd={async (tag, color) => {
                    await handleAddTag(tag, color)
                  }}
                  customButton={
                    <div
                      role="button"
                      className="flex size-6 items-center justify-center rounded-md border border-default"
                    >
                      <Plus width={16} height={16} className="text-default" />
                    </div>
                  }
                  shouldConfirmNewTag={false}
                  containerRef={containerRef}
                  popoverOpen={tagPopoverOpen}
                  onPopoverOpenChange={setTagPopoverOpen}
                />
              )}
            </div>
            <Collapsible.Content>
              {hasTags && (
                <div className="flex grow flex-col gap-y-2" ref={containerRef}>
                  <div className="flex max-h-16 grow flex-wrap gap-1.5 overflow-hidden">
                    {visibleTags.map((tag, i) => (
                      <Tag
                        key={i}
                        workspaceId={contact.workspaceId}
                        model="contacts"
                        tag={tag}
                        removeTag={isAgentOnly ? () => {} : () => handleRemoveTag(tag)}
                        readOnly={isAgentOnly}
                      />
                    ))}
                    {hiddenTags.length > 0 && (
                      <Tooltip
                        message={
                          <div>
                            {hiddenTags.map((tag) => (
                              <div className="py-1" key={tag}>
                                {tag}
                              </div>
                            ))}
                          </div>
                        }
                      >
                        <div className="tag cursor-default px-1.5">{`+${hiddenTags.length}`}</div>
                      </Tooltip>
                    )}
                  </div>
                </div>
              )}
            </Collapsible.Content>
          </Collapsible.Root>
        </div>
      </PopoverAnchor>
    </Popover>
  )
})
